/**
 * List + ListItem primitives.
 *
 * Vertical stack of items with consistent dividers + padding. Feature
 * screens (shopping list, products, offers) compose these to render their
 * row collections.
 *
 * Styling resolves from tokens (CP5). Dividers are implemented as inline
 * `borderTop` on non-first items so a single component can render either
 * a plain list or a bordered card list without extra wrappers.
 *
 * Legacy equivalent: ad-hoc `<ul>` / `<div>` stacks in
 * `ShoppingListView.tsx`, `SettingsHouseholdProducts.tsx`, etc. Consolidated here
 * so the row pattern is reusable.
 */

import type { CSSProperties, HTMLAttributes, LiHTMLAttributes, ReactNode } from 'react';

export interface ListProps extends HTMLAttributes<HTMLUListElement> {
  /** Render a subtle divider between items. Defaults to true. */
  divided?: boolean;
  children?: ReactNode;
}

export function List({
  divided = true,
  style,
  children,
  ...rest
}: ListProps): JSX.Element {
  const composed: CSSProperties = {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    ...style,
  };
  return (
    <ul
      {...rest}
      role="list"
      data-divided={divided || undefined}
      style={composed}
    >
      {children}
    </ul>
  );
}

export interface ListItemProps extends LiHTMLAttributes<HTMLLIElement> {
  /** Clickable row — renders a cursor + hover affordance. */
  interactive?: boolean;
  /** Draw a divider above this item (List sets this automatically). */
  divided?: boolean;
  /** Leading content (icon, checkbox). */
  leading?: ReactNode;
  /** Trailing content (chevron, action button). */
  trailing?: ReactNode;
  children?: ReactNode;
}

export function ListItem({
  interactive = false,
  divided = false,
  leading,
  trailing,
  style,
  children,
  ...rest
}: ListItemProps): JSX.Element {
  const composed: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
    padding: 'var(--space-3) var(--space-4)',
    fontFamily: 'var(--font-family-sans)',
    fontSize: 'var(--font-size-base)',
    color: 'var(--color-text-primary)',
    borderTopWidth: divided ? '0.0625rem' : 0,
    borderTopStyle: 'solid',
    borderTopColor: 'var(--color-border-subtle)',
    cursor: interactive ? 'pointer' : 'default',
    ...style,
  };
  return (
    <li
      {...rest}
      data-interactive={interactive || undefined}
      style={composed}
    >
      {leading ? <span data-list-leading>{leading}</span> : null}
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      {trailing ? <span data-list-trailing>{trailing}</span> : null}
    </li>
  );
}
